import type { Route } from "./+types/lesson";
import Page from "../pages/LessonPage";
import { pageMeta } from "../lib/meta";
import { content, type CourseDetail, type LessonDetail } from "../lib/content";
import { tokenFromRequest } from "../lib/auth";
import { breadcrumbSchema, SITE_URL } from "../components/content/SEO/SEO";
import { excerpt } from "../utils/excerpt";

export interface LessonNav { slug: string; title: string }

export async function loader({ params, request }: Route.LoaderArgs) {
  const token = tokenFromRequest(request);
  const [course, lesson] = await Promise.all([
    content.course(params.slug).catch(() => null) as Promise<CourseDetail | null>,
    content.lesson(params.slug, params.lessonSlug, token).catch(() => null) as Promise<LessonDetail | null>,
  ]);

  const flat = (course?.modules ?? []).flatMap((m) => m.lessons.map((l) => ({ slug: l.slug, title: l.title })));
  const idx = flat.findIndex((l) => l.slug === params.lessonSlug);

  return {
    course,
    lesson,
    prev: idx > 0 ? flat[idx - 1] : null,
    next: idx >= 0 && idx < flat.length - 1 ? flat[idx + 1] : null,
    lessonIndex: idx + 1,
    totalLessons: flat.length,
  };
}

export function meta({ data, params }: Route.MetaArgs) {
  const course = data?.course;
  const lesson = data?.lesson;
  const title = lesson?.title ?? course?.title ?? "Lesson";
  const path = `/courses/${params.slug}/${params.lessonSlug}`;
  // A locked lesson returns no body, so it gets a line built from what the API
  // does hand over. Deliberately not course.shortDescription: that would put the
  // same sentence on all ~110 gated lessons of a course.
  const description =
    excerpt(lesson?.body ?? lesson?.content) ||
    (lesson && course
      ? // Separated rather than joined with a comma: plenty of lesson titles end
        // in a question mark, and "who is driving?, part of…" reads as a typo.
        `${lesson.title} · Part of the ${lesson.module.title} module in ${course.title}.`
      : course?.shortDescription || undefined);

  const jsonLd: Record<string, any>[] = [];
  if (course && lesson) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "LearningResource",
      name: lesson.title,
      description,
      url: `${SITE_URL}${path}`,
      inLanguage: "en",
      isPartOf: {
        "@type": "Course",
        name: course.title,
        url: `${SITE_URL}/courses/${course.slug}`,
      },
      provider: { "@type": "Person", name: "Ehsan Gazar", url: SITE_URL },
      // Paid lessons are crawlable but gated. Declaring the paywall is what keeps
      // serving Googlebot a preview from counting as cloaking; the selector has to
      // match the region LessonPage swaps out for the unlock card.
      isAccessibleForFree: !lesson.locked,
      ...(lesson.locked
        ? {
            hasPart: {
              "@type": "WebPageElement",
              isAccessibleForFree: false,
              cssSelector: "#lesson-body",
            },
          }
        : {}),
    });
    jsonLd.push(
      breadcrumbSchema([
        { name: "Home", url: SITE_URL },
        { name: "Courses", url: `${SITE_URL}/courses` },
        { name: course.title, url: `${SITE_URL}/courses/${course.slug}` },
        { name: lesson.title, url: `${SITE_URL}${path}` },
      ]),
    );
  }

  return pageMeta({
    title: `${title} | Gazar`,
    description,
    path,
    image: (course?.image as any)?.url,
    type: "article",
    jsonLd: jsonLd.length ? jsonLd : undefined,
  });
}

export default function R({ loaderData }: Route.ComponentProps) { return <Page {...loaderData} />; }
